import dayjs from 'dayjs';
import React, { useMemo } from 'react';

import DonationsPrimaryActions from '@/core/components/DonationDetails/DonationActions/DonationsPrimaryActions';
import DonationsSecondaryActions from '@/core/components/DonationDetails/DonationActions/DonationsSecondaryActions';
import DonationCategory from '@/core/components/DonationDetails/DonationCategory';
import DonationCriteria from '@/core/components/DonationDetails/DonationCriteria';
import DonationDescription from '@/core/components/DonationDetails/DonationDescription';
import DonationsUser from '@/core/components/DonationDetails/DonationsUser';
import FavoriteButton from '@/core/components/DonationDetails/FavoriteButton';
import DonationStateTag from '@/core/components/DonationStateTag';
import Header from '@/core/components/Header';
import NewCarouselImages from '@/core/components/NewCarouselImages';
import SchemaOrg from '@/core/components/SchemaOrg';
import useDisclosure from '@/core/hooks/useDisclosure';
import { useComponentsContext } from '@/core/lib/components/components.context';
import { isToday, timeFromDate } from '@/core/lib/dayjs/helpers';
import type DonationDomain from '@/core/lib/new-architecture/domain/donation.domain';
import Store from '@/core/lib/new-architecture/store';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

interface DonationDetailsProps {
  donation?: DonationDomain;
  onBack: () => void;
}

const DonationDetails: React.FC<DonationDetailsProps> = ({ donation, onBack }) => {
  const { MetaTitle } = useComponentsContext();
  const { t } = useTranslationContext(['common', 'donation-detail']);
  const publicDonation = donation?.publicDonation;
  const privateDonation = donation?.privateDonation;

  const city = donation?.getCityLabel();
  const distance = donation?.getDistance();
  const solicitations = donation?.getSolicitations();

  const { category, description, title, date, photos, signature, options } = useMemo(() => {
    const donationDomain = privateDonation ?? publicDonation;
    if (!donationDomain) return { photos: [], options: [] };

    const { cat, description, title, last_published_at, photos, signature } = donationDomain.donation;

    return {
      category: cat,
      description,
      title,
      date: last_published_at,
      photos,
      signature,
      options: donationDomain.options || [],
    };
  }, [privateDonation, publicDonation]);

  const user = donation?.getUser();

  const { isOpen: isActionsOpen, onOpen: onOpenActions, onClose: onCloseActions } = useDisclosure();

  if (!publicDonation && !privateDonation) {
    return null;
  }

  const { personae } = donation;
  const { primary, secondary } = donation.getActions();
  const productSchema = donation.getProductSchema();
  const webpageSchema = donation.getWebpageSchema();
  const breadcrumbListSchema = donation.getBreadcrumbListSchema(t);
  const seo = donation.getSeo();
  const state = donation.getState();
  const favoriteAction = donation.getFavoriteAction();

  return (
    <>
      <SchemaOrg schema={productSchema} />
      <SchemaOrg schema={webpageSchema} />
      <SchemaOrg schema={breadcrumbListSchema} />
      <MetaTitle meta={seo} />

      <div className="relative w-full">
        <Header
          content=""
          iconBefore="arrow-left"
          onBefore={onBack}
          iconAfter={secondary.length > 0 ? 'more-vertical' : undefined}
          onAfter={secondary.length > 0 ? onOpenActions : undefined}
          hasPadding
        />
        <DonationsSecondaryActions actions={secondary} isOpen={isActionsOpen} onClose={onCloseActions} />
      </div>

      <article className="w-full text-content-primary">
        <section className="relative">
          {favoriteAction && (
            <div className="absolute right-2 top-2 z-10">
              <FavoriteButton action={favoriteAction} />
            </div>
          )}
          <NewCarouselImages photos={photos} />
        </section>
        <div className="m-5 space-y-6 lg:m-8 lg:space-y-10">
          <div className="space-y-6">
            <section className="text-body-secondary space-y-4">
              <div className="space-y-2">
                <DonationStateTag state={state} personae={personae} />
                <h1 className="text-title font-semibold">{title}</h1>
                {dayjs(date).isValid() && <p>{t(isToday(dayjs(date)) ? 'donation-time' : 'donation-date', { ns: 'donation-detail', date: timeFromDate(dayjs(date)) })}</p>}

                <p>{[city, distance].filter(Boolean).join(' - ')}</p>
                {!!solicitations && <p>{t('solicitations', { ns: 'donation-detail', count: solicitations })}</p>}
              </div>
              <DonationsPrimaryActions actions={primary} />
            </section>
            <hr className="text-stroke-secondary" />
            <section className="space-y-4">
              <DonationCategory category={category} />
              <DonationCriteria options={options} />
              <DonationDescription description={description} />
              <FurtherInformation signature={signature} />
            </section>
            <hr className="text-stroke-secondary" />
            <DonationAds />
          </div>
          <DonationsUser uuid={user?.uuid} idFiltered={donation.getId()} />
        </div>
      </article>
    </>
  );
};

const FurtherInformation: React.FC<{ signature?: string | null }> = ({ signature }) => {
  const { t } = useTranslationContext(['common']);
  if (!signature) return null;
  return (
    <section className="space-y-2 lg:space-y-3">
      <h2 className="text-body-primary font-semibold text-content-primary">{t('components.donation.details.further-information.title', { ns: 'common' })}</h2>
      <div className="text-body-secondary rounded-2 bg-bg-pale p-3 text-content-placeholder">{signature}</div>
    </section>
  );
};

const DonationAds = () => {
  const { data: current } = Store.current.useCurrent();

  if (!current || current.displayAds()) {
    return (
      <section className="mb-6 mt-4 flex w-full flex-col items-center justify-around lg:flex-row">
        <div className="hidden min-h-[310px] w-[340px] lg:block" dangerouslySetInnerHTML={{ __html: '<div class="Content_1" style="display:none;"></div>' }}></div>
        <div className="hidden min-h-[310px] w-[340px] lg:block" dangerouslySetInnerHTML={{ __html: '<div class="Content_2" style="display:none;"></div>' }}></div>
        <div className="min-h-[310px] w-[320px] lg:hidden" dangerouslySetInnerHTML={{ __html: '<div class="Mobile_Pos1" style="display:none;"></div>' }}></div>
      </section>
    );
  }
  return null;
};

export default DonationDetails;
